import ms from 'ms';

const makeCurve = (points) => points.map(([x0, y0], idx) => {
  if (idx - 1 < 0) {
    return [x0, [y0, 1]];
  }
  const [x1, y1] = points[idx - 1];
  const slope = (y1 - y0) / (x1 - x0);
  return [x0, [y0, slope]];
});

const findInCurve = (curve, x, fn = (x1, x2) => x1 <= x2) => {
  const [x2, line] = curve.find(([currY]) => fn(currY, x));
  const [b, slope] = line;
  return slope * (x - x2) + b;
};

const defaultDistribution = {
  activity: makeCurve([
    [Number.MIN_SAFE_INTEGER, 0],
    [0, 0],
    [ms('3m'), 0.025],
    [ms('7m'), 0.5],
    [ms('10m'), 1],
    [ms('15m'), 0.9],
    [ms('30m'), 0.7],
    [ms('1h'), 0.5],
    [ms('3h'), 0.25],
    [ms('1d'), 0.15],
    [ms('2d'), 0.125],
    [ms('1w'), 0.1],
    [ms('3w'), 0.075],
    [ms('25d'), 0],
    [Number.MAX_SAFE_INTEGER, 0],
  ]),
  rating: makeCurve([
    [1, 1],
    [0.9, 0.95],
    [0.8, 0.7],
    [0.7, 0.3],
    [0.6, 0.05],
    [0.4, 0],
    [-Number.MAX_SAFE_INTEGER, 0],
  ]),
};

const createActivityDistribution = (meanTime) => {
  // We multiply the mean time between chats by the number of expected chats
  // before the same expert has high probability of being requested again.
  // The result is put inside a window to avoid extreme values skewing the
  // distribution too much.
  const mean = Math.min(Math.max(5 * meanTime, ms('8m')), ms('30m'));

  const q1 = mean * 0.25;
  const q2 = mean * 0.5;
  const q3 = mean * 0.75;
  const q4 = mean * 1.25;

  const mean2 = 2 * mean;
  const meanH = 4 * mean; // ~ 1 hour
  const mean3 = 3 * meanH;
  const meanD = 8 * mean3; // ~ 1 day
  const mean4 = 4 * meanD;
  const meanW = 7 * meanD; // ~ 1 week
  const mean5 = 2 * meanW;

  return makeCurve([
    [Number.MIN_SAFE_INTEGER, 0],
    [0, 0],
    [q1, 0.01],
    [q2, 0.2],
    [q3, 0.7],
    [mean, 1],
    [q4, 1],
    [mean2, 0.95],
    [meanH, 0.75],
    [mean3, 0.5],
    [meanD, 0.5],
    [mean4, 0.1],
    [meanW, 0.05],
    [mean5, 0.02],
    [ms('29d'), 0],
    [Number.MAX_SAFE_INTEGER, 0],
  ]);
};

const createRatingDistribution = (avgRating) => {
  // Put the average rating in a window to avoid skewing the distribution
  // too much in extreme cases.
  const mean = Math.max(Math.min(avgRating, 0.95), 0.7);
  const q1 = mean + (1 - mean) * 0.7;
  const q2 = mean + (1 - mean) * 0.3;
  const q3 = mean * 0.95;
  const q4 = mean * 0.9;
  const q5 = mean * 0.8;

  return makeCurve([
    [1, 1],
    [q1, 0.95],
    [q2, 0.75],
    [mean, 0.4],
    [q3, 0.2],
    [q4, 0.05],
    [q5, 0],
    [Number.MIN_SAFE_INTEGER, 0],
  ]);
};

export {
  findInCurve,
  createActivityDistribution,
  createRatingDistribution,
  defaultDistribution,
};
